import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Brain, ShieldCheck, Clock, Activity, Heart, Users, Check, Play } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import AdhdFocusSection from '../components/AdhdFocusSection';
import MedicalClinicSchema from '../components/MedicalClinicSchema';

const DEFAULT_HERO_IMAGE = 'https://assets.auxodev.dk/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/psykiatrisk-udredning-koebenhavn-desktop-v4_218841e5.jpg';
const DEFAULT_HERO_MOBILE_IMAGE = 'https://assets.auxodev.dk/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/felix-enghoff-klich-martin-bergholtz-nordisk-psykiatri-mobil.jpg';

export default function Home() {
  const { t, lang, getLocalizedValue, localizePath } = useLanguage();
  const [cmsData, setCmsData] = useState(null);

  useEffect(() => {
    const loadCms = async () => {
      try {
        const [resForside, resYdelser] = await Promise.all([
          fetch('https://api.dev.auxo.dk/public/content/forside_side'),
          fetch('https://api.dev.auxo.dk/public/v2/content/forside_ydelser?sort=sortering&order=asc')
        ]);
        if (resForside.ok) {
          const data = await resForside.json();
          if (Array.isArray(data) && data.length > 0) {
            setCmsData(data[0]);
          }
        }
        if (resYdelser.ok) {
          const jsonYdelser = await resYdelser.json();
          if (jsonYdelser?.items && Array.isArray(jsonYdelser.items) && jsonYdelser.items.length > 0) {
            setCmsServices(jsonYdelser.items);
          }
        }
      } catch (e) {
        console.error('CMS fetch error on Home:', e);
      }
    };
    loadCms();
  }, []);

  const [cmsServices, setCmsServices] = useState(null);

  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlayVideo = () => {
    const v = videoRef.current;
    if (!v) return;

    v.controls = true;

    const playPromise = v.play();
    if (playPromise && playPromise.catch) {
      playPromise.catch((err) => {
        console.warn('Video playback notice:', err);
      });
    }
    setIsPlaying(true);
  };

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const handleEnded = () => {
      v.pause();
      v.currentTime = 0;
      v.controls = false;
      setIsPlaying(false);
    };

    v.addEventListener('ended', handleEnded);

    return () => {
      v.removeEventListener('ended', handleEnded);
    };
  }, []);

  const seoTitle = lang === 'da' 
    ? 'Nordisk Psykiatri - Udredning og behandling i trygge rammer' 
    : 'Nordisk Psykiatri - Assessment and treatment in safe surroundings';

  const seoDesc = lang === 'da'
    ? 'Vi tilbyder grundig udredning og målrettet behandling inden for ADHD, autisme og andre psykiatriske lidelser med høj faglighed og mennesket i centrum.'
    : 'We offer thorough assessment and targeted treatment for ADHD, autism, and other psychiatric conditions with high clinical expertise and the individual at the center.';

  useDocumentMetadata(seoTitle, seoDesc, { lang });

  const defaultServices = [
    {
      num: '01',
      title: 'ADHD',
      desc: lang === 'da' 
        ? 'Udredning og behandling af ADHD hos børn, unge og voksne.'
        : 'Assessment and treatment of ADHD in adolescents and adults.',
      link: '/adhd',
      buttonText: lang === 'da' ? 'Læs mere' : 'Read more',
      image: 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/chatgpt-image-aug-7-2026-10-35-29-am-1.png'
    },
    {
      num: '02',
      title: 'AUTISME',
      desc: lang === 'da'
        ? 'Udredning og rådgivning ved mistanke om autismespektrumforstyrrelse.'
        : 'Assessment and guidance upon suspicion of autism spectrum conditions.',
      link: '/autisme',
      buttonText: lang === 'da' ? 'Læs mere' : 'Read more',
      image: 'https://assets.auxodev.dk/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/chatgpt-image-sep-4-2026-11-26-34-am.png'
    },
    {
      num: '03',
      title: 'UDREDNING',
      desc: lang === 'da'
        ? 'Grundig og systematisk udredning tilpasset dine individuelle behov.'
        : 'Thorough and systematic assessment tailored to your individual needs.',
      link: '/udredning',
      buttonText: lang === 'da' ? 'Læs mere' : 'Read more',
      image: 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/chatgpt-image-aug-7-2026-10-35-30-am-3.png'
    },
    {
      num: '04',
      title: 'MEDICINSK BEHANDLING',
      desc: lang === 'da'
        ? 'Vurdering, opstart og tæt opfølgning af medicinsk behandling, hvis det vurderes relevant.'
        : 'Evaluation, initiation, and close follow-up of medical treatment when relevant.',
      link: '/medicinsk-behandling',
      buttonText: lang === 'da' ? 'Læs mere' : 'Read more',
      image: 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/chatgpt-image-aug-7-2026-10-35-31-am-4.png'
    },
    {
      num: '05',
      title: 'PSYKOTERAPI &\nPSYKOEDUKATION',
      desc: lang === 'da' 
        ? 'Samtaleforløb og psykoedukation med fokus på forståelse, mestring og varig forandring.'
        : 'Therapy and psychoeducation focused on insight, coping, and lasting change.',
      link: '/om-os',
      buttonText: lang === 'da' ? 'Læs mere' : 'Read more',
      image: 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/chatgpt-image-aug-7-2026-10-35-32-am-5_06097b4a.png'
    }
  ];

  const readMorePills = [
    {
      label: lang === 'da' ? 'ADHD-udredning' : 'ADHD assessment',
      link: '/adhd'
    },
    {
      label: lang === 'da' ? 'Autismeudredning' : 'Autism assessment',
      link: '/autisme'
    },
    {
      label: lang === 'da' ? 'Medicinsk behandling' : 'Medical treatment',
      link: '/medicinsk-behandling'
    },
    {
      label: lang === 'da' ? 'Priser & forløb' : 'Prices & conditions',
      link: '/priser'
    }
  ];

  const services = cmsServices && cmsServices.length > 0
    ? cmsServices.map((item, idx) => {
        let imgUrl = item.billede?.[0] || 'https://assets.auxodev.dk/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/chatgpt-image-aug-7-2026-10-35-29-am-1.png';
        if (imgUrl.includes('assets.auxodev.dk') && !imgUrl.includes('/cdn-cgi/image/')) {
          imgUrl = imgUrl.replace('https://assets.auxodev.dk/', 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/');
        }
        const rawTitle = item.titel ? getLocalizedValue(item.titel) : '';
        const rawDesc = item.beskrivelse ? getLocalizedValue(item.beskrivelse) : '';
        const rawBtn = item.knap_tekst ? getLocalizedValue(item.knap_tekst) : (lang === 'da' ? 'Læs mere' : 'Read more');
        return {
          id: item.id || idx,
          num: item.nummer || String(idx + 1).padStart(2, '0'),
          title: rawTitle || defaultServices[idx]?.title || '',
          desc: rawDesc || defaultServices[idx]?.desc || '',
          link: item.link || defaultServices[idx]?.link || '#',
          buttonText: rawBtn || (lang === 'da' ? 'Læs mere' : 'Read more'),
          image: imgUrl
        };
      })
    : defaultServices;

  return (
    <div className="flex flex-col min-h-screen bg-sand-light">
      <MedicalClinicSchema />
      {/* 1. HERO SECTION */}
      <section className="relative w-full bg-white overflow-hidden pt-8 sm:pt-12 lg:py-20 pb-0 lg:min-h-[720px] xl:min-h-[780px] flex items-center border-none outline-none">
        
        {/* Full-bleed Background Image filling the entire hero on desktop */}
        <div className="hidden lg:block absolute inset-0 w-full h-full z-0 overflow-hidden pointer-events-none select-none">
          <img
            src={(() => {
              const rawImg = cmsData?.hero_billede?.[0];
              if (!rawImg || rawImg.includes('scandinavian-forest')) return DEFAULT_HERO_IMAGE;
              return rawImg;
            })()}
            alt={cmsData?.hero_overskrift ? getLocalizedValue(cmsData.hero_overskrift) : "Nordisk Psykiatri - Psykolog og Psykiater"}
            className="w-full h-full object-cover object-right"
            loading="eager"
            fetchPriority="high"
          />
        </div>

        {/* Text Container aligned with site grid */}
        <div className="w-full max-w-[1536px] mx-auto px-6 lg:px-12 relative z-10 flex flex-col justify-center">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 items-center w-full">
            
            {/* Left Text Area (~50% width on desktop) */}
            <div className="lg:col-span-6 xl:col-span-5 pr-0 lg:pr-6 flex flex-col justify-center">
              
              {/* Main Headline */}
              <h1 className="font-display text-[34px] sm:text-[44px] lg:text-[50px] xl:text-[58px] font-medium text-petrol-dark leading-[1.12] tracking-tight mb-5 max-w-[620px]">
                {cmsData?.hero_overskrift ? getLocalizedValue(cmsData.hero_overskrift) : cmsData?.hero_title ? getLocalizedValue(cmsData.hero_title) : (lang === 'da' ? 'Psykiatrisk udredning og behandling med mennesket i centrum' : 'Utredning och behandling med hög professionalism och människan i centrum')}
              </h1>

              {/* Short Decorative Line */}
              <div className="w-12 h-[2px] bg-petrol-dark/30 mb-6" />

              {/* Body Text */}
              <div className="text-base sm:text-lg text-petrol-dark/85 leading-relaxed max-w-[540px] mb-8 font-sans [&_a]:font-bold [&_a]:underline [&_a]:underline-offset-2 [&_a]:text-petrol-dark hover:[&_a]:text-petrol-medium transition-colors">
                {cmsData?.hero_undertitel ? (
                  <div dangerouslySetInnerHTML={{ __html: getLocalizedValue(cmsData.hero_undertitel) }} />
                ) : cmsData?.hero_subtitle ? (
                  <div dangerouslySetInnerHTML={{ __html: getLocalizedValue(cmsData.hero_subtitle) }} />
                ) : (
                  <p>{lang === 'da' ? 'Vi tilbyder grundig psykiatrisk udredning og målrettet behandling af ADHD, autisme og andre psykiatriske lidelser hos unge og voksne i København – med mulighed for at gennemføre et udredningsforløb inden for 2–3 uger fra første samtale.' : 'Vi erbjuder noggrann utredning och riktad behandling inom ADHD, autism och andra psykiatriska tillstånd – med möjlighet att genomföra en utredning inom 3–4 veckor från första samtalet.'}</p>
                )}
              </div>

              {/* CTAs */}
              <div className="flex flex-wrap gap-3.5 sm:gap-4 items-center mb-8">
                <Link
                  to={localizePath(cmsData?.cta_knap_link || "/kontakt")}
                  className="inline-flex items-center gap-2.5 px-7 py-3.5 bg-petrol-dark text-sand-light text-xs sm:text-sm font-semibold rounded-full hover:bg-petrol-medium transition-all duration-300 shadow-xs group"
                >
                  <span>{cmsData?.cta_knap_tekst ? getLocalizedValue(cmsData.cta_knap_tekst) : 'Kontakt os om booking'}</span>
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                </Link>

                <Link
                  to={localizePath("/udredning")}
                  className="inline-flex items-center gap-2 px-6 py-3.5 border border-petrol-dark/30 text-petrol-dark text-xs sm:text-sm font-semibold rounded-full hover:bg-petrol-dark/5 hover:border-petrol-dark/60 transition-all duration-300 group"
                >
                  <span>{lang === 'da' ? 'Læs om vores forløb' : 'Read about our process'}</span>
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                </Link>
              </div>

              {/* Reassurance points */}
              <div className="pt-5 border-t border-petrol-dark/15 flex flex-wrap items-center gap-y-2.5 gap-x-6 text-xs sm:text-sm text-petrol-dark/80 font-medium">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#223a35] shrink-0" aria-hidden="true" />
                  <span>{cmsData?.hero_tag_1 ? getLocalizedValue(cmsData.hero_tag_1) : (lang === 'da' ? 'Færdigudredt på max 2-3 uger' : 'Fully assessed within 3-4 weeks')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#223a35] shrink-0" aria-hidden="true" />
                  <span>{cmsData?.hero_tag_2 ? getLocalizedValue(cmsData.hero_tag_2) : (lang === 'da' ? 'Psykiater og psykolog samlet ét sted' : 'Psychologist and psychiatrist working closely')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#223a35] shrink-0" aria-hidden="true" />
                  <span>{cmsData?.hero_tag_3 ? getLocalizedValue(cmsData.hero_tag_3) : (lang === 'da' ? 'Ingen henvisning kræves' : 'No referral required')}</span>
                </div>
              </div>

              {/* Mobile/Tablet image placed below the text */}
              <div className="mt-8 sm:mt-10 lg:hidden -mx-6 sm:-mx-6 overflow-hidden">
                <img
                  src={(() => {
                    const rawMobileImg = cmsData?.hero_billede_mobil?.[0];
                    if (rawMobileImg) return rawMobileImg;
                    return DEFAULT_HERO_MOBILE_IMAGE;
                  })()}
                  alt="Felix - Nordisk Psykiatri"
                  className="w-full h-auto max-h-[480px] sm:max-h-[540px] object-cover object-top"
                  loading="eager"
                  fetchPriority="high"
                />
              </div>
            </div>

            {/* Empty right column on grid so left text stays aligned */}
            <div className="lg:col-span-6 xl:col-span-7" />

          </div>
        </div>

      </section>

      {/* 2. SECTION: HVAD KAN VI HJÆLPE MED? */}
      <section id="udredning" className="py-16 lg:py-24 bg-sand-light">
        <div className="mx-auto max-w-[1536px] px-6 lg:px-12">
          
          {/* Section Header */}
          <div className="mb-8 lg:mb-12">
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-petrol-dark mb-4">
              {cmsData?.ydelser_overskrift ? getLocalizedValue(cmsData.ydelser_overskrift) : 'Hvad kan vi hjælpe med?'}
            </h2>
            <div className="w-[60px] h-[2px] bg-sand-medium/90 border-t border-petrol-medium/30" />
          </div>

          {/* Cohesive Service Columns */}
          <div className="border border-[#D1E1BE] rounded-none bg-sand-light shadow-none overflow-hidden">
            <div className={`grid grid-cols-1 sm:grid-cols-2 ${services.length <= 3 ? 'lg:grid-cols-3' : services.length === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-5'} divide-y sm:divide-y-0 sm:divide-x divide-[#D1E1BE]`}>
              {services.map((item) => (
                <article key={item.id || item.num} className="flex flex-col p-5 sm:p-6 hover:bg-sand-light/40 transition-colors duration-300 group h-full">
                  
                  {/* Service Image */}
                  <div className="relative h-44 sm:h-48 overflow-hidden mb-6 bg-sand-medium/20">
                    <img
                      src={item.image}
                      alt={item.title.replace('\n', ' ')}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                    />
                  </div>

                  {/* Number */}
                  <div className="text-xs font-sans font-medium text-petrol-medium/75 tracking-wider mb-2">
                    {item.num}
                  </div>

                  {/* Title */}
                  <h3 className="font-sans text-sm font-bold tracking-wider text-petrol-dark uppercase mb-3 min-h-[40px] flex items-start whitespace-pre-line leading-snug">
                    {item.title}
                  </h3>

                  {/* Description */}
                  <p className="text-xs sm:text-sm text-petrol-dark/75 leading-relaxed mb-8 flex-grow font-sans">
                    {item.desc}
                  </p>

                  {/* Read More Link */}
                  <Link
                    to={localizePath(item.link)}
                    className="inline-flex items-center gap-2 text-xs font-semibold tracking-wider text-petrol-dark uppercase hover:text-petrol-medium mt-auto pt-2 group/link"
                  >
                    <span>{item.buttonText || (lang === 'da' ? 'Læs mere' : 'Read more')}</span>
                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover/link:translate-x-1" aria-hidden="true" />
                  </Link>

                </article>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* =========================================================================
          PRESERVED SECTIONS AFTER THE 5 SERVICE COLUMNS (UNTOUCHED BELOW THIS POINT)
         ========================================================================= */}

      {/* Why Choose Us Section */}
      <section className="relative overflow-hidden py-10 sm:py-12 lg:pt-[25px] lg:pb-[40px] bg-[#fcfcfa] text-[#123b35] font-sans">
        <div className="relative z-[2] w-[calc(100%-32px)] sm:w-[calc(100%-48px)] max-w-[1320px] mx-auto">
          {/* Header */}
          <div className="max-w-[730px] mb-7 sm:mb-8 lg:mb-[30px]">
            <h2 className="font-display text-[30px] sm:text-[36px] lg:text-[42px] font-medium tracking-[-1.4px] leading-[1.2] text-[#123b35] m-0 mb-4 after:content-[''] after:block after:w-[50px] after:h-[2px] after:bg-[#bdd1b3] after:mt-2">
              {cmsData?.hvorfor_overskrift ? getLocalizedValue(cmsData.hvorfor_overskrift) : t('home.why_title')}
            </h2>
            <p className="text-[15px] sm:text-[16px] leading-[1.65] text-[#344e49] m-0 font-sans">
              {cmsData?.hvorfor_undertekst ? getLocalizedValue(cmsData.hvorfor_undertekst) : (lang === 'da' ? 'Vi tror på, at grundig udredning kræver tid, tæt samarbejde og et menneskeligt blik på hele din situation. Derfor arbejder vi tværfagligt og lægger vægt på, at du får en tryg, individuelt tilpasset udredning og behandling, så du oplever sammenhæng og tydelig retning videre.' : t('home.why_subtitle'))}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[330px_minmax(0,1fr)] xl:grid-cols-[370px_minmax(0,1fr)] gap-6 items-stretch">
            {/* Video Card */}
            <div
              onClick={!isPlaying ? handlePlayVideo : undefined}
              className="relative overflow-hidden rounded-[10px] w-full h-full min-h-[460px] sm:min-h-[520px] lg:min-h-0 bg-[#243d36] cursor-pointer group/video select-none"
              title={lang === 'da' ? 'Afspil video' : 'Play video'}
            >
              {/* Native video element */}
              <video
                id="felix-video"
                ref={videoRef}
                preload="none"
                poster="https://assets.auxodev.dk/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/felix-video-lysere.jpg"
                style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', cursor: 'pointer' }}
                className="w-full h-full object-cover object-top absolute inset-0 cursor-pointer"
                playsInline
              >
                <source src="https://assets.auxodev.dk/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/felix-video-endelig.mp4" type="video/mp4" />
              </video>

              {/* Poster image on top before play */}
              <img
                src="https://assets.auxodev.dk/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/felix-video-lysere.jpg"
                alt="Felix Enghoff Klich - Nordisk Psykiatri"
                style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }}
                className={`w-full h-full object-cover object-top absolute inset-0 brightness-95 transition-opacity duration-300 ${
                  isPlaying ? 'opacity-0 pointer-events-none' : 'opacity-100'
                }`}
              />

              {/* Gradient overlay */}
              <div
                className={`absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/35 pointer-events-none transition-opacity duration-300 ${
                  isPlaying ? 'opacity-0' : 'opacity-100'
                }`}
              />

              {/* Center Play Button & label */}
              <div
                className={`absolute z-[2] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-3 text-white transition-opacity duration-300 ${
                  isPlaying ? 'opacity-0 pointer-events-none' : 'opacity-100'
                }`}
              >
                <span className="w-[70px] h-[70px] rounded-full bg-white text-[#103c34] flex items-center justify-center text-2xl shadow-[0_5px_25px_rgba(0,0,0,0.12)] transition-transform duration-300 group-hover/video:scale-110">
                  ▶
                </span>
                <span className="font-medium text-sm text-white drop-shadow-xs">
                  {lang === 'da' ? 'Se video' : 'Watch video'}
                </span>
              </div>

              {/* Bottom Floating Pill CTA */}
              <div
                className={`absolute z-[2] bottom-[17px] left-1/2 -translate-x-1/2 transition-opacity duration-300 ${
                  isPlaying ? 'opacity-0 pointer-events-none' : 'opacity-100'
                }`}
              >
                <Link
                  to={localizePath("/om-os")}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-2 whitespace-nowrap bg-white text-[#153832] font-medium text-[13px] px-[19px] py-[9px] rounded-[40px] shadow-[0_3px_12px_rgba(0,0,0,0.08)] transition-all duration-200 hover:bg-[#f2f5f1] hover:-translate-y-0.5 active:translate-y-0"
                >
                  {lang === 'da' ? 'Læs mere om os' : 'Read more about us'}
                  <span className="text-[13px] transition-transform duration-200 group-hover:translate-x-0.5">→</span>
                </Link>
              </div>
            </div>

            {/* Right Feature Grid (3 Cols on Desktop) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-[19px] gap-y-[13px]">
              {/* Card 1 */}
              <article
                className="min-w-0 p-[24px_21px] rounded-[10px] transition-all duration-300 hover:-translate-y-[5px] hover:shadow-[0_12px_30px_rgba(31,65,44,0.1)] flex flex-col justify-between"
                style={{ background: 'linear-gradient(135deg, #d8e6ce 0%, #e4eedb 48%, #d5e4ca 100%)' }}
              >
                <div>
                  <div className="h-[65px] sm:h-[76px] flex items-start select-none">
                    <img
                      src="https://assets.auxodev.dk/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/blade-pastel-ikon.png"
                      alt={cmsData?.hvorfor_1_titel ? getLocalizedValue(cmsData.hvorfor_1_titel) : t('home.why_1_title')}
                      className="w-[58px] h-[58px] sm:w-[68px] sm:h-[68px] object-contain pointer-events-none select-none mix-blend-multiply"
                    />
                  </div>
                  <h3 className="font-display text-[19px] font-medium leading-[1.35] tracking-[-0.4px] text-[#143b34] mt-1.5 mb-[15px]">
                    {cmsData?.hvorfor_1_titel ? getLocalizedValue(cmsData.hvorfor_1_titel) : t('home.why_1_title')}
                  </h3>
                  <p className="m-0 text-[#657166] text-[14px] leading-[1.6] font-sans">
                    {cmsData?.hvorfor_1_tekst ? getLocalizedValue(cmsData.hvorfor_1_tekst) : t('home.why_1_desc')}
                  </p>
                </div>
              </article>

              {/* Card 2 */}
              <article
                className="min-w-0 p-[24px_21px] rounded-[10px] transition-all duration-300 hover:-translate-y-[5px] hover:shadow-[0_12px_30px_rgba(31,65,44,0.1)] flex flex-col justify-between"
                style={{ background: 'linear-gradient(135deg, #d8e6ce 0%, #e4eedb 48%, #d5e4ca 100%)' }}
              >
                <div>
                  <div className="h-[65px] sm:h-[76px] flex items-start select-none">
                    <img
                      src="https://assets.auxodev.dk/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/bue-pastel-ikon.png"
                      alt={cmsData?.hvorfor_2_titel ? getLocalizedValue(cmsData.hvorfor_2_titel) : t('home.why_2_title')}
                      className="w-[58px] h-[58px] sm:w-[68px] sm:h-[68px] object-contain pointer-events-none select-none mix-blend-multiply"
                    />
                  </div>
                  <h3 className="font-display text-[19px] font-medium leading-[1.35] tracking-[-0.4px] text-[#143b34] mt-1.5 mb-[15px]">
                    {cmsData?.hvorfor_2_titel ? getLocalizedValue(cmsData.hvorfor_2_titel) : t('home.why_2_title')}
                  </h3>
                  <p className="m-0 text-[#657166] text-[14px] leading-[1.6] font-sans">
                    {cmsData?.hvorfor_2_tekst ? getLocalizedValue(cmsData.hvorfor_2_tekst) : t('home.why_2_desc')}
                  </p>
                </div>
              </article>

              {/* Card 3 */}
              <article
                className="min-w-0 p-[24px_21px] rounded-[10px] transition-all duration-300 hover:-translate-y-[5px] hover:shadow-[0_12px_30px_rgba(31,65,44,0.1)] flex flex-col justify-between"
                style={{ background: 'linear-gradient(135deg, #d8e6ce 0%, #e4eedb 48%, #d5e4ca 100%)' }}
              >
                <div>
                  <div className="h-[65px] sm:h-[76px] flex items-start select-none">
                    <img
                      src="https://assets.auxodev.dk/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/filer-pastel-ikon.png"
                      alt={cmsData?.hvorfor_3_titel ? getLocalizedValue(cmsData.hvorfor_3_titel) : (lang === 'da' ? 'Psykiatrisk udredning på 4 uger' : 'Assessment in 4 weeks')}
                      className="w-[58px] h-[58px] sm:w-[68px] sm:h-[68px] object-contain pointer-events-none select-none mix-blend-multiply"
                    />
                  </div>
                  <h3 className="font-display text-[19px] font-medium leading-[1.35] tracking-[-0.4px] text-[#143b34] mt-1.5 mb-[15px]">
                    {cmsData?.hvorfor_3_titel ? getLocalizedValue(cmsData.hvorfor_3_titel) : (lang === 'da' ? 'Psykiatrisk udredning på 4 uger' : 'Assessment in 4 weeks')}
                  </h3>
                  <p className="m-0 text-[#657166] text-[14px] leading-[1.6] font-sans">
                    {cmsData?.hvorfor_3_tekst ? getLocalizedValue(cmsData.hvorfor_3_tekst) : (lang === 'da' ? 'ADHD- og autismeudredning i København med kort ventetid – typisk færdigudredt inden for 4 uger, uden henvisning.' : t('home.why_3_desc'))}
                  </p>
                </div>
              </article>

              {/* Card 4 */}
              <article
                className="min-w-0 p-[24px_21px] rounded-[10px] transition-all duration-300 hover:-translate-y-[5px] hover:shadow-[0_12px_30px_rgba(31,65,44,0.1)] flex flex-col justify-between"
                style={{ background: 'linear-gradient(135deg, #d8e6ce 0%, #e4eedb 48%, #d5e4ca 100%)' }}
              >
                <div>
                  <div className="h-[65px] sm:h-[76px] flex items-start select-none">
                    <img
                      src="https://assets.auxodev.dk/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/ba-lge-pastel-ikon.png"
                      alt={cmsData?.hvorfor_4_titel ? getLocalizedValue(cmsData.hvorfor_4_titel) : t('home.why_4_title')}
                      className="w-[58px] h-[58px] sm:w-[68px] sm:h-[68px] object-contain pointer-events-none select-none mix-blend-multiply"
                    />
                  </div>
                  <h3 className="font-display text-[19px] font-medium leading-[1.35] tracking-[-0.4px] text-[#143b34] mt-1.5 mb-[15px]">
                    {cmsData?.hvorfor_4_titel ? getLocalizedValue(cmsData.hvorfor_4_titel) : t('home.why_4_title')}
                  </h3>
                  <p className="m-0 text-[#657166] text-[14px] leading-[1.6] font-sans">
                    {cmsData?.hvorfor_4_tekst ? getLocalizedValue(cmsData.hvorfor_4_tekst) : t('home.why_4_desc')}
                  </p>
                </div>
              </article>

              {/* Card 5 */}
              <article
                className="min-w-0 p-[24px_21px] rounded-[10px] transition-all duration-300 hover:-translate-y-[5px] hover:shadow-[0_12px_30px_rgba(31,65,44,0.1)] flex flex-col justify-between"
                style={{ background: 'linear-gradient(135deg, #d8e6ce 0%, #e4eedb 48%, #d5e4ca 100%)' }}
              >
                <div>
                  <div className="h-[65px] sm:h-[76px] flex items-start select-none">
                    <img
                      src="https://assets.auxodev.dk/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/multubue-pastel-ikon.png"
                      alt={cmsData?.hvorfor_5_titel ? getLocalizedValue(cmsData.hvorfor_5_titel) : t('home.why_5_title')}
                      className="w-[58px] h-[58px] sm:w-[68px] sm:h-[68px] object-contain pointer-events-none select-none mix-blend-multiply"
                    />
                  </div>
                  <h3 className="font-display text-[19px] font-medium leading-[1.35] tracking-[-0.4px] text-[#143b34] mt-1.5 mb-[15px]">
                    {cmsData?.hvorfor_5_titel ? getLocalizedValue(cmsData.hvorfor_5_titel) : t('home.why_5_title')}
                  </h3>
                  <p className="m-0 text-[#657166] text-[14px] leading-[1.6] font-sans">
                    {cmsData?.hvorfor_5_tekst ? getLocalizedValue(cmsData.hvorfor_5_tekst) : t('home.why_5_desc')}
                  </p>
                </div>
              </article>

              {/* Card 6 */}
              <article
                className="min-w-0 p-[24px_21px] rounded-[10px] transition-all duration-300 hover:-translate-y-[5px] hover:shadow-[0_12px_30px_rgba(31,65,44,0.1)] flex flex-col justify-between"
                style={{ background: 'linear-gradient(135deg, #d8e6ce 0%, #e4eedb 48%, #d5e4ca 100%)' }}
              >
                <div>
                  <div className="h-[65px] sm:h-[76px] flex items-start select-none">
                    <img
                      src="https://assets.auxodev.dk/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/trappe-pastel-ikon.png"
                      alt={cmsData?.hvorfor_6_titel ? getLocalizedValue(cmsData.hvorfor_6_titel) : t('home.why_6_title')}
                      className="w-[58px] h-[58px] sm:w-[68px] sm:h-[68px] object-contain pointer-events-none select-none mix-blend-multiply"
                    />
                  </div>
                  <h3 className="font-display text-[19px] font-medium leading-[1.35] tracking-[-0.4px] text-[#143b34] mt-1.5 mb-[15px]">
                    {cmsData?.hvorfor_6_titel ? getLocalizedValue(cmsData.hvorfor_6_titel) : t('home.why_6_title')}
                  </h3>
                  <p className="m-0 text-[#657166] text-[14px] leading-[1.6] font-sans">
                    {cmsData?.hvorfor_6_tekst ? getLocalizedValue(cmsData.hvorfor_6_tekst) : t('home.why_6_desc')}
                  </p>
                </div>
              </article>
            </div>
          </div>
        </div>

        {/* Decorative Wave at the Bottom */}
        <svg
          className="absolute left-0 bottom-0 w-full h-[220px] sm:h-[260px] lg:h-[300px] pointer-events-none z-[1]"
          viewBox="0 0 1440 300"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path
            fill="#dce9d2"
            opacity=".65"
            d="M0,90 C200,20 350,190 570,150 C820,100 940,300 1160,160 C1300,80 1380,110 1440,90 L1440,300 L0,300 Z"
          />
          <path
            fill="#bdd3ad"
            opacity=".45"
            d="M0,160 C180,60 340,170 550,210 C780,290 970,280 1170,180 C1320,100 1390,130 1440,120 L1440,300 L0,300 Z"
          />
        </svg>
      </section>

      {/* Process Section */}
      <section id="forloeb" className="py-16 lg:py-24 bg-sand-light">
        <div className="mx-auto max-w-[1180px] px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-petrol-medium block mb-2">
              {cmsData?.forloeb_badge ? getLocalizedValue(cmsData.forloeb_badge) : 'UDREDNINGSFORLØB'}
            </span>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium text-petrol-dark mb-3">
              {cmsData?.forloeb_overskrift ? getLocalizedValue(cmsData.forloeb_overskrift) : t('home.process_title')}
            </h2>
            <p className="text-sm sm:text-base text-text-muted leading-relaxed">
              {cmsData?.forloeb_undertekst ? getLocalizedValue(cmsData.forloeb_undertekst) : t('home.process_subtitle')}
            </p>
            <div className="w-10 h-0.5 bg-petrol-medium/30 mx-auto mt-4" />
          </div>

          {/* Horizontal Process steps */}
          <div className="relative">
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-sand-medium/60 -translate-y-1/2 z-0" />
            
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 relative z-10">
              {[
                { 
                  step: '01', 
                  title: cmsData?.forloeb_1_titel ? getLocalizedValue(cmsData.forloeb_1_titel) : t('home.process_step_1'), 
                  desc: cmsData?.forloeb_1_tekst ? getLocalizedValue(cmsData.forloeb_1_tekst) : t('home.process_step_1_desc') 
                },
                { 
                  step: '02', 
                  title: cmsData?.forloeb_2_titel ? getLocalizedValue(cmsData.forloeb_2_titel) : t('home.process_step_2'), 
                  desc: cmsData?.forloeb_2_tekst ? getLocalizedValue(cmsData.forloeb_2_tekst) : t('home.process_step_2_desc') 
                },
                { 
                  step: '03', 
                  title: cmsData?.forloeb_3_titel ? getLocalizedValue(cmsData.forloeb_3_titel) : t('home.process_step_3'), 
                  desc: cmsData?.forloeb_3_tekst ? getLocalizedValue(cmsData.forloeb_3_tekst) : t('home.process_step_3_desc') 
                },
                { 
                  step: '04', 
                  title: cmsData?.forloeb_4_titel ? getLocalizedValue(cmsData.forloeb_4_titel) : t('home.process_step_4'), 
                  desc: cmsData?.forloeb_4_tekst ? getLocalizedValue(cmsData.forloeb_4_tekst) : t('home.process_step_4_desc') 
                },
                { 
                  step: '05', 
                  title: cmsData?.forloeb_5_titel ? getLocalizedValue(cmsData.forloeb_5_titel) : t('home.process_step_5'), 
                  desc: cmsData?.forloeb_5_tekst ? getLocalizedValue(cmsData.forloeb_5_tekst) : t('home.process_step_5_desc') 
                },
              ].map((item) => (
                <div key={item.step} className="bg-sand-light/60 border border-sand-medium/60 rounded-xl p-5 sm:p-6 flex flex-col items-center text-center">
                  <div className="w-11 h-11 rounded-full bg-petrol-dark text-white font-semibold text-sm sm:text-base flex items-center justify-center mb-4 shadow-sm">
                    {item.step}
                  </div>
                  <h3 className="font-display text-lg sm:text-xl font-bold text-petrol-dark mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-text-muted leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Vores Særlige Fokus: ADHD-behandling i København */}
          <div className="mt-14 lg:mt-20">
            <AdhdFocusSection lang={lang} />
          </div>

          {/* Secondary Action Bar ("Læs mere") */}
          <div className="mt-14 lg:mt-20">
            <div className="bg-[#edf1ed] border border-[#d6dfd6] rounded-2xl p-6 sm:p-8 lg:p-9 flex flex-col lg:flex-row lg:items-center justify-between gap-6 shadow-xs">
              <div className="shrink-0 max-w-sm">
                <h3 className="font-display text-2xl sm:text-3xl text-petrol-dark font-normal tracking-tight mb-1">
                  {cmsData?.laes_mere_overskrift ? getLocalizedValue(cmsData.laes_mere_overskrift) : (lang === 'da' ? 'Læs mere' : 'Read more')}
                </h3>
                <p className="text-xs sm:text-sm text-text-muted leading-relaxed">
                  {cmsData?.laes_mere_undertekst ? getLocalizedValue(cmsData.laes_mere_undertekst) : (lang === 'da' ? 'Udforsk relaterede emner og få mere information.' : 'Explore related topics and get more information.')}
                </p>
              </div>

              {/* Right Pill Actions - single line */}
              <div className="flex items-center flex-nowrap gap-2 sm:gap-2.5 lg:gap-3 overflow-x-auto scrollbar-none shrink-0 py-1">
                {readMorePills.map((pill) => (
                  <Link
                    key={pill.link}
                    to={localizePath(pill.link)}
                    className="inline-flex items-center gap-1.5 sm:gap-2 px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-full border border-petrol-dark/60 bg-white/70 sm:bg-transparent text-petrol-dark text-xs sm:text-[13px] font-medium whitespace-nowrap shrink-0 hover:bg-petrol-dark hover:text-white hover:border-petrol-dark transition-all duration-200 group"
                  >
                    <span>{pill.label}</span>
                    <span className="text-xs transition-transform group-hover:translate-x-0.5">→</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 lg:py-20 bg-[#1e3a35] text-white border-t border-b border-[#162d29]">
        <div className="mx-auto max-w-[1180px] px-6 text-center">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium mb-4 text-white">
            {cmsData?.cta_overskrift ? getLocalizedValue(cmsData.cta_overskrift) : t('common.contact_cta_title')}
          </h2>
          <p className="text-base text-white/85 leading-relaxed max-w-2xl mx-auto mb-8">
            {cmsData?.cta_tekst ? getLocalizedValue(cmsData.cta_tekst) : t('common.contact_cta_text')}
          </p>
          <Link
            to={localizePath(cmsData?.cta_knap_link || "/kontakt")}
            className="inline-flex items-center gap-3 px-8 py-4 bg-white text-petrol-dark font-semibold rounded-full hover:bg-sand-light transition-colors duration-300 shadow-md group"
          >
            <span>{cmsData?.cta_knap_tekst ? getLocalizedValue(cmsData.cta_knap_tekst) : 'Kontakt os om booking'}</span>
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </div>
  );
}
